import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useCampaigns, useCreateCampaign, useUpdateCampaign } from '../api/hooks';
import type { Campaign } from '../api/types';
import { EmptyState, ErrorBanner, Modal, TableSkeleton } from '../components/ui';

const PLATFORM_LABEL: Record<Campaign['platform'], string> = {
  other: 'Portal',
  meta: 'Meta',
  google_ads: 'Google Ads',
  linkedin: 'LinkedIn',
};

/**
 * Settings → Campaigns. A campaign has to exist here, and be active, before a
 * lead can be given it. Deactivating stops new assignments; leads keep it.
 */
export function CampaignsPage() {
  const { data: campaigns = [], isPending, error } = useCampaigns(true);
  const update = useUpdateCampaign();
  const [editing, setEditing] = useState<Campaign | 'new' | null>(null);
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState<unknown>(null);

  const active = campaigns.filter((c) => c.is_active);
  const inactive = campaigns.filter((c) => !c.is_active);

  async function toggle(c: Campaign) {
    setNotice('');
    setActionError(null);
    try {
      await update.mutateAsync({ id: c.id, is_active: !c.is_active });
      setNotice(c.is_active ? `"${c.name}" deactivated. Its leads keep it.` : `"${c.name}" activated.`);
    } catch (err) {
      setActionError(err);
    }
  }

  const row = (c: Campaign) => (
    <tr key={c.id}>
      <td style={c.is_active ? undefined : { opacity: 0.65 }}>
        <Link to={`/leads?campaign=${c.id}`}><b>{c.name}</b></Link>
      </td>
      <td><span className="chip chip-plain">{PLATFORM_LABEL[c.platform] ?? c.platform}</span></td>
      <td className="num">{c.lead_count.toLocaleString()}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {c.platform !== 'meta' && (
          <button className="btn btn-sm" style={{ marginRight: 6 }} onClick={() => { setNotice(''); setEditing(c); }}>
            Rename
          </button>
        )}
        <button
          className={`btn btn-sm${c.is_active ? ' btn-danger' : ''}`}
          disabled={update.isPending}
          onClick={() => toggle(c)}
        >
          {c.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </td>
    </tr>
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Campaigns</h1>
          <p className="sub">
            {active.length} active {active.length === 1 ? 'campaign' : 'campaigns'} · only active
            campaigns can be assigned to leads
          </p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => { setNotice(''); setEditing('new'); }}>
            + Add campaign
          </button>
        </div>
      </div>

      {notice && <div className="banner banner-success" role="status">{notice}</div>}
      <ErrorBanner error={error ?? actionError} />

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Source</th>
              <th className="num">Leads</th>
              <th />
            </tr>
          </thead>
          {isPending ? <TableSkeleton rows={5} cols={4} /> : <tbody>{active.map(row)}</tbody>}
        </table>
      </div>
      {!isPending && active.length === 0 && (
        <EmptyState title="No active campaigns">Add one so it can be assigned to leads.</EmptyState>
      )}

      {inactive.length > 0 && (
        <>
          <h2 className="section-title">Deactivated</h2>
          <p className="sub" style={{ marginTop: -6 }}>
            Cannot be assigned to new leads. Leads that already have one keep it, and the Leads
            filter still finds them. Campaigns that arrive from Meta land here until you activate them.
          </p>
          <div className="table-wrap">
            <table className="data"><tbody>{inactive.map(row)}</tbody></table>
          </div>
        </>
      )}

      {editing && (
        <CampaignModal
          campaign={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); setNotice(msg); }}
        />
      )}
    </div>
  );
}

function CampaignModal({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: Campaign | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const [name, setName] = useState(campaign?.name ?? '');
  const [error, setError] = useState<unknown>(null);
  const busy = create.isPending || update.isPending;
  const invalid = error instanceof ApiError && Boolean(error.fieldErrors.name);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (campaign) {
        await update.mutateAsync({ id: campaign.id, name: name.trim() });
        onSaved(`Renamed to "${name.trim()}".`);
      } else {
        const created = await create.mutateAsync({ name: name.trim() });
        onSaved(`"${created.name}" added. It can now be assigned to leads.`);
      }
    } catch (err) {
      setError(err);
    }
  }

  return (
    <Modal
      title={campaign ? `Rename "${campaign.name}"` : 'Add campaign'}
      onClose={onClose}
      width={460}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="campaign-form" disabled={busy || !name.trim()}>
            {busy ? <span className="spinner" /> : campaign ? 'Save' : 'Add campaign'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form id="campaign-form" onSubmit={submit}>
        <div className={`field${invalid ? ' invalid' : ''}`}>
          <label htmlFor="cmp-name">Campaign name<span className="req">*</span></label>
          <input
            id="cmp-name"
            autoFocus
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sisya 1-1 Campaign"
          />
        </div>
      </form>
    </Modal>
  );
}
